import {IMachineryWithLocation} from '../../../models/iMachinery';
import {isStationaryMachineryType, MAIN_SILENT_FOR_BEACON_MS} from '../../machinery/utils/const';
import {MarkerMode} from './machineryMarkerShapes';
import {TrackerStatus} from '../../../models/ITracker';

// ─── Пороги (перенос из MapMarkers, значения без изменений) ───────────────
export const OFFLINE_THRESHOLD_MS = 10 * 60 * 1000;    // 10 минут без данных → offline
/**
 * Стационарная техника (ДЭС) шлёт точки редко, 10-минутный порог давал
 * ложный «Нет связи».
 */
export const OFFLINE_THRESHOLD_STATIONARY_MS = 2 * 60 * 60 * 1000;

/**
 * Порог «нет связи» для типа техники. Единственный источник порога:
 * маркер на карте и карточка в панели обязаны считать одинаково.
 */
export function offlineThresholdMs(typeId?: number): number {
    return isStationaryMachineryType(typeId)
        ? OFFLINE_THRESHOLD_STATIONARY_MS
        : OFFLINE_THRESHOLD_MS;
}
export const SPEED_MOVING_THRESHOLD = 0;                 // > 0 km/h → moving
export const SPEED_WARNING_KMH = 110;                    // превышение скорости
export const VOLTAGE_DANGER_THRESHOLD = 3;                // обрыв питания
export const VOLTAGE_WARNING_THRESHOLD = 12.2;            // низкое напряжение
export const COOLANT_DANGER_TEMP_C = 110;                 // перегрев
export const FUEL_WARNING_LITERS = 10;                    // мало топлива

// ─── Типы состояния ───────────────────────────────────────────────────────
export type DangerAlert = 'voltage_cut' | 'overheat' | 'engine_check' | null;
export type WarnAlert = 'fuel' | 'speeding' | 'low_voltage' | null;

export interface MarkerState {
    mode: MarkerMode;
    dangerAlert: DangerAlert;
    warnAlert: WarnAlert;
    isApproximate: boolean;
    /**
     * Машина в режиме заморозки (основной трекер FROZEN). Отдельный признак,
     * а не значение mode: MarkerMode общий с историей маршрутов, где
     * заморозки нет. Маркер по нему рисует снежинку вместо «?».
     */
    isFrozen: boolean;
}

/**
 * Состояние машины по её данным. Перенос из MapMarkers.getMarkerState 1:1:
 *   mode: offline (возраст записи > OFFLINE_THRESHOLD_MS) → moving (speed>0) →
 *         idling (двигатель работает) → parked;
 *   danger: обрыв питания / перегрев / check-engine;
 *   warn:   низкое напряжение / мало топлива / превышение скорости;
 *   isApproximate: координата ненадёжна (LBS вместо GPS).
 */
export function getMarkerState(m: IMachineryWithLocation): MarkerState {
    const now = Date.now();
    const age = m.recorded_at_ms ? now - m.recorded_at_ms : Infinity;
    const isOffline = age > offlineThresholdMs(m.type_id);
    // Заморожена — приём данных отключён намеренно, поэтому возраст записи
    // у неё всегда «просрочен». mode остаётся offline (форма шестигранника
    // верна), но маркер и панель по этому признаку скажут «Заморожена»,
    // а не «Нет связи».
    const isFrozen = m.tracker_status === TrackerStatus.FROZEN;

    const mode: MarkerMode = isOffline
        ? 'offline'
        : (m.speed_kmh ?? 0) > SPEED_MOVING_THRESHOLD
            ? 'moving'
            : m.engine_running
                ? 'idling'
                : 'parked';

    const dangerAlert: DangerAlert =
        (m.board_voltage !== undefined && m.board_voltage < VOLTAGE_DANGER_THRESHOLD)
            ? 'voltage_cut'
            : (m.coolant_temp_c !== undefined && m.coolant_temp_c > COOLANT_DANGER_TEMP_C)
                ? 'overheat'
                : m.engine_is_check
                    ? 'engine_check'
                    : null;

    const warnAlert: WarnAlert =
        (m.board_voltage !== undefined
            && m.board_voltage >= VOLTAGE_DANGER_THRESHOLD
            && m.board_voltage < VOLTAGE_WARNING_THRESHOLD)
            ? 'low_voltage'
            : (m.fuel_level_liter !== undefined && m.fuel_level_liter < FUEL_WARNING_LITERS)
                ? 'fuel'
                : (m.speed_kmh !== undefined && m.speed_kmh > SPEED_WARNING_KMH)
                    ? 'speeding'
                    : null;

    return {
        mode,
        // У замороженной машины тревоги считаются из точки основного трекера,
        // а она застыла на моменте заморозки — «обрыв питания» там появился
        // из-за самой заморозки, а не из-за угона. Гасим их, как и панель
        // (MachineryPanelItem.getAlerts): иначе красный маркер сигналил бы об
        // аварии там, где ничего не произошло, и перекрывал бы «морозный»
        // цвет с ❄. Заодно замороженная перестаёт попадать в счётчик тревог
        // в заголовке вкладки (DangerTitleAlert).
        dangerAlert: isFrozen ? null : dangerAlert,
        warnAlert: isFrozen ? null : warnAlert,
        isApproximate: m.coord_approximate === true,
        isFrozen,
    };
}

/**
 * Бейдж тревоги (правый верхний угол тела, радиус/позиция от sz). Перенос
 * инлайн-кода из MapMarkers.createMachineryIcon без изменений:
 *   danger → красный кружок с мигающим «!» (класс mm-dbadge),
 *   warn   → жёлтый кружок с «!»,
 *   иначе  → пусто.
 */
export function buildAlertBadgeSvg(s: MarkerState, sz: number): string {
    const hasDanger = s.dangerAlert !== null;
    const hasWarn = s.warnAlert !== null;
    const br = sz * 0.175;
    const bx = sz - br - 1;
    const by = br + 1;

    if (hasDanger) return `
        <g class="mm-dbadge">
            <circle cx="${bx}" cy="${by}" r="${br}" fill="#E53935" stroke="white" stroke-width="1.8"/>
            <text x="${bx}" y="${by}" text-anchor="middle" dominant-baseline="central"
                font-size="${br * 1.45}" font-family="sans-serif" font-weight="900" fill="white">!</text>
        </g>`;
    if (hasWarn) return `
        <circle cx="${bx}" cy="${by}" r="${br}" fill="#F59E0B" stroke="white" stroke-width="1.8"/>
        <text x="${bx}" y="${by}" text-anchor="middle" dominant-baseline="central"
            font-size="${br * 1.45}" font-family="sans-serif" font-weight="900" fill="white">!</text>`;
    return '';
}

/**
 * «Волны» приблизительной координаты — три расходящихся оранжевых круга
 * (классы mm-aw1/2/3). Перенос инлайн-кода из MapMarkers без изменений.
 */
export function buildApproxWavesSvg(isApproximate: boolean, sz: number): string {
    if (!isApproximate) return '';
    const cx = sz / 2;
    return `
        <circle class="mm-aw1" cx="${cx}" cy="${cx}" r="${cx + 2}" fill="#FF8C42" stroke="none"/>
        <circle class="mm-aw2" cx="${cx}" cy="${cx}" r="${cx + 2}" fill="#FF8C42" stroke="none"/>
        <circle class="mm-aw3" cx="${cx}" cy="${cx}" r="${cx + 2}" fill="#FF8C42" stroke="none"/>`;
}

/**
 * CSS-анимации бейджа тревоги (мигание) и волн приблизительной координаты.
 * Определения идентичны блоку в MapMarkers.ensureGlobalStyles — тот же вид.
 * Отдельный style-id: MapMarkers инъектит свой блок под своим id (его не
 * трогаем), эта функция — для MapLibre-версии. Идемпотентна.
 */
export function ensureMarkerAlertStyles(): void {
    if (typeof document === 'undefined') return;
    const id = 'machinery-marker-alert-styles';
    if (document.getElementById(id)) return;
    const style = document.createElement('style');
    style.id = id;
    style.textContent = `
        @keyframes mm-approx-wave {
            0%   { opacity: 0.5; }
            60%  { opacity: 0.2; }
            100% { opacity: 0;   }
        }
        .mm-aw1 { animation: mm-approx-wave 2.5s ease-out infinite 0s; }
        .mm-aw2 { animation: mm-approx-wave 2.5s ease-out infinite 0.85s; }
        .mm-aw3 { animation: mm-approx-wave 2.5s ease-out infinite 1.7s; }

        @keyframes mm-danger-flash {
            0%,100% { opacity: 1; }
            50%     { opacity: 0.55; }
        }
        .mm-dbadge { animation: mm-danger-flash 1s ease-in-out infinite; }
    `;
    document.head.appendChild(style);
}

// ─── Аварийный маяк ───────────────────────────────────────────────────────

export interface EffectivePosition {
    latitude: number;
    longitude: number;
    /** true — позиция с аварийного маяка (основной молчит, точка маяка свежее). */
    isBeacon: boolean;
    /**
     * true — показанная координата приблизительная (LBS по вышкам).
     * Для маяка — beacon_coord_approximate, для основного — coord_approximate:
     * признак всегда от ТОГО источника, чьи координаты показаны.
     */
    isApproximate: boolean;
    /** Оценка точности приблизительной координаты, м (если источник сообщил). */
    accuracyM?: number;
}

/**
 * Показывать ли позицию машины по аварийному маяку. Все условия — И:
 *   - основной трекер молчит: дольше MAIN_SILENT_FOR_BEACON_MS, либо не
 *     выходил на связь вовсе (recorded_at_ms == null), либо машина
 *     ЗАМОРОЖЕНА — тогда молчание постоянное by design и порог ждать нечего
 *     (приём пакетов основного отключён на входе, см. FROZEN-гейт в
 *     TrackerDataProcessorService);
 *   - точка маяка свежее точки основного (строго; или основной без точки);
 *   - у маяка есть координаты (last-known; null — контакты были только
 *     без GPS-фикса).
 *
 * Без ветки заморозки два часа после заморозки карта показывала позицию и
 * время ОСНОВНОГО трекера, хотя маяк уже присылал свежие точки и карточка
 * машины их видела.
 *
 * nowMs — параметром для детерминированных тестов.
 */
export function shouldShowBeacon(
    m: IMachineryWithLocation,
    nowMs: number = Date.now(),
): boolean {
    const mainSilent =
        m.tracker_status === TrackerStatus.FROZEN ||
        m.recorded_at_ms == null ||
        nowMs - m.recorded_at_ms > MAIN_SILENT_FOR_BEACON_MS;

    const beaconFresher =
        m.beacon_recorded_at_ms != null &&
        (m.recorded_at_ms == null || m.beacon_recorded_at_ms > m.recorded_at_ms);

    return (
        mainSilent &&
        beaconFresher &&
        m.beacon_latitude != null &&
        m.beacon_longitude != null
    );
}

/**
 * Эффективная позиция маркера машины: маяк по правилу shouldShowBeacon,
 * иначе основной трекер, иначе null (рисовать нечего). Покрывает и машину
 * «только с маяком» (основной ни разу не выходил: latitude == null, но
 * beacon-координаты есть) — раньше такой маркер не рисовался бы вовсе.
 * ЕДИНСТВЕННЫЙ источник координаты для маркера, стартового кадра, fitBounds
 * и центрирования — карта и панель обязаны считать одинаково.
 * isApproximate/accuracyM — от выбранного источника (правило "все показания —
 * от точки, чьи координаты показываем").
 */
export function resolveEffectivePosition(
    m: IMachineryWithLocation,
    nowMs: number = Date.now(),
): EffectivePosition | null {
    if (shouldShowBeacon(m, nowMs)) {
        return {
            latitude: m.beacon_latitude as number,
            longitude: m.beacon_longitude as number,
            isBeacon: true,
            isApproximate: m.beacon_coord_approximate === true,
            accuracyM: m.beacon_coord_accuracy_m,
        };
    }
    if (m.latitude != null && m.longitude != null) {
        return {
            latitude: m.latitude,
            longitude: m.longitude,
            isBeacon: false,
            isApproximate: m.coord_approximate === true,
        };
    }
    return null;
}

/**
 * Бейдж «данные с аварийного маяка» — янтарный кружок с глифом радиоволн в
 * правом нижнем углу тела маркера. Углы распределены: тип техники — слева
 * снизу, тревога — справа сверху, маяк — справа снизу, конфликтов нет.
 * Форма маркера при показе по маяку всегда шестигранник offline (основной
 * молчит дольше любого offline-порога), стрелка сюда не «заметает».
 * Цвет — константа «чернил карты», как у соседних бейджей (не токен темы).
 */
export function buildBeaconBadgeSvg(sz: number): string {
    const br = sz * 0.175;
    const bx = sz - br - 1;
    const by = sz - br - 1;
    const cy = by + br * 0.28; // точка-передатчик чуть ниже центра кружка
    const arc = (r: number) => `
        <path d="M ${bx - r} ${cy} A ${r} ${r} 0 0 1 ${bx + r} ${cy}"
            fill="none" stroke="white" stroke-width="${br * 0.22}" stroke-linecap="round"/>`;
    return `
        <g>
            <circle cx="${bx}" cy="${by}" r="${br}" fill="#F59E0B" stroke="white" stroke-width="1.8"/>
            <circle cx="${bx}" cy="${cy}" r="${br * 0.18}" fill="white"/>
            ${arc(br * 0.45)}
            ${arc(br * 0.78)}
        </g>`;
}